import type { Job } from "@/api/hooks/useJobs";
import type { User } from "@/api/hooks/useMe";
import type { UnitStatus } from "@/api/raptor";
import { useUpdate } from "@mb-pro-ui/utils";
import { AccessTime, LocationOn, Person, Schedule } from "@mui/icons-material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Slide,
  Snackbar,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "wouter";
import { AppHeader } from "../components/AppHeader";
import { formatDateTime } from "../components/formatDate";
import { statusConfig } from "../constants/status";

type JobListProps = {
  jobs: Job[];
  isLoading: boolean;
  refetch: () => void;
  me: User | undefined;
};

export const JobList = ({ jobs, isLoading, refetch, me }: JobListProps) => {
  const intl = useIntl();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const activeJobs = useMemo(
    () => jobs?.filter((job) => job["own-status"]?.state !== "pending"),
    [jobs],
  );

  const pendingJobs = useMemo(
    () =>
      jobs
        ?.filter((job) => job["own-status"]?.state === "pending")
        .sort(
          (a, b) =>
            (a["created-ts"] ? new Date(a["created-ts"]).getTime() : 0) -
            (b["created-ts"] ? new Date(b["created-ts"]).getTime() : 0),
        ) ?? [],
    [jobs],
  );

  const mutation = useUpdate<UnitStatus>("raptor/unit-statuses", {
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: intl.formatMessage({ defaultMessage: "Job accepted" }),
        severity: "success",
      });
      refetch();
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: intl.formatMessage({
          defaultMessage: "An error occured, please try again!",
        }),
        severity: "error",
      });
    },
  });

  const acceptJob = (jobId: string) => {
    const id = jobs.find((j) => j.id === jobId)?.["own-status"].id;
    if (id) {
      mutation.mutate({ id: id, state: "accepted" });
    } else {
      setSnackbar({
        open: true,
        message: intl.formatMessage({
          defaultMessage: "An error occured, please try again!",
        }),
        severity: "error",
      });
    }
  };

  if (isLoading) {
    return (
      <Box
        color="grey.400"
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          px: 2,
          textAlign: "center",
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (!jobs?.length) {
    return (
      <>
        <AppHeader me={me} />
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            px: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              mb: 3,
              background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Schedule sx={{ fontSize: 40, color: "#9ca3af" }} />
          </Box>
          <Typography variant="h6" sx={{ color: "#6b7280", fontWeight: 600 }}>
            {intl.formatMessage({ defaultMessage: "No active jobs" })}
          </Typography>
          <Typography variant="body1" sx={{ color: "#9ca3af", mt: 1 }}>
            {intl.formatMessage({
              defaultMessage: "New jobs will be displayed here",
            })}
          </Typography>
        </Box>
      </>
    );
  }

  const renderJobCard = (job: Job) => (
    <ListItem key={job.id} sx={{ p: 0, mb: 2 }}>
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          p: 3,
          borderRadius: 3,
          background: "#ffffff",
          border: "1px solid #f0f0f0",
        }}
      >
        <Typography
          variant="raptor-sub-h"
          sx={{
            mb: 3,
          }}
        >
          {job.details.intervention["start-event"]["localized-description"]}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NotificationImportantIcon
                sx={{ fontSize: 20, color: "#ED6C02" }}
              />
            </Box>
            <Typography variant="raptor-main-p" sx={{ flex: 1 }}>
              {job.details.intervention["start-event"].category}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Person sx={{ fontSize: 20, color: "primary.main" }} />
            </Box>
            <Typography variant="raptor-main-p">
              {job.details.intervention.customer.name}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocationOn sx={{ fontSize: 20, color: "#2e7d32" }} />
            </Box>
            <Typography
              variant="raptor-main-p"
              sx={{
                wordBreak: "break-all",
              }}
            >
              {job.details.intervention.customer.address}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccessTime sx={{ fontSize: 20, color: "#f57c00" }} />
            </Box>
            <Typography variant="raptor-main-p">
              {formatDateTime(intl, job["created-ts"])}
            </Typography>
          </Box>

          {job["own-status"].state !== "pending" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {job["own-status"].state === "accepted" ? (
                  <PriorityHighIcon color="error" sx={{ fontSize: 20 }} />
                ) : (
                  <CheckCircleOutline
                    sx={{
                      fontSize: 20,
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                )}
              </Box>
              <Typography variant="raptor-main-p">
                {intl.formatMessage(statusConfig[job["own-status"].state])}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {!["accepted", "started", "arrived"].includes(
              job["own-status"].state,
            ) && (
              <Button variant="contained" onClick={() => acceptJob(job.id)}>
                <Typography variant="raptor-button-text">
                  {intl.formatMessage({ defaultMessage: "Confirm" })}
                </Typography>
              </Button>
            )}
            <Link
              key={job.id}
              to={`jobs/${job.id}`}
              style={{
                flex: 1,
                display: "flex",
                textDecoration: "none",
              }}
            >
              <Button
                variant="contained"
                fullWidth
                sx={{
                  minHeight: 48,
                  "&:hover": {
                    backgroundColor: "#195b7e",
                    color: "white",
                    borderColor: "#195b7e",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Typography variant="raptor-button-text">
                  {intl.formatMessage({ defaultMessage: "Job details" })}
                </Typography>
              </Button>
            </Link>
          </Box>
        </Box>
      </Paper>
    </ListItem>
  );

  return (
    <>
      <AppHeader me={me} />
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          pt: "65px",
          px: 2,
          backgroundColor: "#f8f9fa",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pb: 2,
            "-webkit-overflow-scrolling": "touch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              p: 2,
              backgroundColor: "white",
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography variant="raptor-main-h">
              {intl.formatMessage({ defaultMessage: "Task" })}
            </Typography>
            <Typography
              color="primary"
              sx={{ fontWeight: 500 }}
            >{`${jobs.length}`}</Typography>
          </Box>
          <List sx={{ p: 0 }}>
            {activeJobs.length > 0 && (
              <>
                <Typography
                  variant="raptor-main-h"
                  sx={{
                    color: "#2e7d32",
                    mb: 2,
                    ml: 1,
                  }}
                >
                  {activeJobs.length > 1
                    ? intl.formatMessage({ defaultMessage: "Active jobs" })
                    : intl.formatMessage({ defaultMessage: "Active job" })}
                </Typography>
                {activeJobs.map((job) => renderJobCard(job))}
              </>
            )}

            {pendingJobs.length > 0 && (
              <>
                <Typography
                  variant="raptor-main-h"
                  sx={{
                    color: "#f57c00",
                    mb: 2,
                    ml: 1,
                    mt: activeJobs ? 3 : 0,
                  }}
                >
                  {intl.formatMessage({ defaultMessage: "Pending" })}
                </Typography>
                {pendingJobs.map((job) => renderJobCard(job))}
              </>
            )}
          </List>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          slots={{ transition: Slide }}
        >
          <Alert
            severity={snackbar.severity as "success" | "error"}
            sx={{
              borderRadius: 3,
              "& .MuiAlert-icon": {
                fontSize: "1.2rem",
              },
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};
